import { Alert } from "antd";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { ROUTES } from "@/config/routes";
import { anchorApi } from "@/lib/anchorApi";
import { useAuthStore } from "@/store/authStore";
import type { ReactNode } from "react";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const restored = useAuthStore((state) => state.restored);
  const updateAnchor = useAuthStore((state) => state.updateAnchor);
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [profileCheckError, setProfileCheckError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!restored || !isAuthenticated) {
      return;
    }
    let canceled = false;
    setCheckingProfile(true);
    setProfileCheckError(null);
    anchorApi
      .getProfile()
      .then((response) => {
        if (canceled || response.code !== 0 || !response.data) {
          if (!canceled) {
            setProfileCheckError(
              response.message || "Failed to verify profile",
            );
          }
          return;
        }
        const completed = Boolean(response.data.profile_completed);
        updateAnchor({
          ...response.data.profile,
          profile_completed: completed,
        });
        if (!completed && location.pathname !== ROUTES.profileCompletion) {
          navigate(ROUTES.profileCompletion, { replace: true });
        } else if (
          completed &&
          location.pathname === ROUTES.profileCompletion
        ) {
          navigate(ROUTES.workspace, { replace: true });
        }
      })
      .catch((error) => {
        if (!canceled) {
          setProfileCheckError(
            error instanceof Error ? error.message : "Failed to verify profile",
          );
        }
      })
      .finally(() => {
        if (!canceled) {
          setCheckingProfile(false);
        }
      });
    return () => {
      canceled = true;
    };
  }, [isAuthenticated, location.pathname, navigate, restored, updateAnchor]);

  if (!restored) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  if (checkingProfile) {
    return null;
  }

  if (profileCheckError && location.pathname !== ROUTES.profileCompletion) {
    return (
      <div
        style={{
          minHeight: "100%",
          display: "grid",
          placeItems: "center",
          padding: 24,
        }}
      >
        <Alert
          message="Profile verification failed"
          description={profileCheckError}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return <>{children}</>;
}
